/**
 * Small, non-interactive "this is what such a file looks like" spreadsheet
 * snippet: a column-letter gutter row (A, B, C…) plus a row-number gutter on
 * the left, purely illustrative. Distinct from `HeaderRowSelector`'s raw-rows
 * picker grid (`apps/web`) — this renders fixed example content only, meant
 * for help/explainer copy that needs to visually read as "a real Excel file"
 * rather than a generic app table.
 */
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './table.js';
import { cn } from '../lib/utils.js';

export interface ExampleFileSnippetProps {
  /** Header row cell values, e.g. `['Symbol', 'Data od', 'Data do', 'Status']`. */
  headers: string[];
  /** Example data rows below the header row, each aligned to `headers`. */
  rows: string[][];
  className?: string;
  testId?: string;
}

/** Base-26 spreadsheet-style column letters (A, B, … Z, AA, AB, …). */
function columnLetter(index: number): string {
  let n = index;
  let label = '';
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

export function ExampleFileSnippet({ headers, rows, className, testId }: ExampleFileSnippetProps) {
  return (
    <div
      className={cn('overflow-hidden rounded-md border border-border/60', className)}
      data-testid={testId}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              caps={false}
              className="w-8 bg-muted/70 px-2 py-1 text-center text-[10px] text-muted-foreground/60"
              aria-hidden="true"
            />
            {headers.map((_, index) => (
              // Static, purely illustrative column set (never reordered) — index-as-key
              // matches the TableSkeletonRows precedent for fixed-length placeholder rows.
              <TableHead
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                caps={false}
                className="bg-muted/70 px-3 py-1 text-center text-[10px] text-muted-foreground/60"
              >
                {columnLetter(index)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell className="border-r border-border/40 bg-muted/30 px-2 py-1.5 text-center text-2xs tabular-nums text-muted-foreground/70">
              1
            </TableCell>
            {headers.map((header, index) => (
              <TableCell
                // eslint-disable-next-line react/no-array-index-key -- static illustrative content, see comment above
                key={index}
                className="whitespace-nowrap px-3 py-1.5 text-[12px] font-semibold text-foreground"
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
          {rows.map((row, rowIndex) => (
            <TableRow
              // eslint-disable-next-line react/no-array-index-key -- static illustrative content, see comment above
              key={rowIndex}
            >
              <TableCell className="border-r border-border/40 bg-muted/30 px-2 py-1.5 text-center text-2xs tabular-nums text-muted-foreground/70">
                {rowIndex + 2}
              </TableCell>
              {row.map((cell, cellIndex) => (
                <TableCell
                  // eslint-disable-next-line react/no-array-index-key -- static illustrative content, see comment above
                  key={cellIndex}
                  className="whitespace-nowrap px-3 py-1.5 text-[12px] text-foreground"
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
